import { useLazyQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { usePuppyMode } from '../../../contexts/PuppyModeContext';
import type {
  UsersPageSearchBuyerEntityQuery,
  UsersPageSearchBuyerEntityQueryVariables,
  UsersPageBuyerEntityFragmentFragment,
} from '../../../generated';
import { BuyerSelectOption } from '../components/BuyerLinking/types';
import { getSelectOptionFromBuyer } from '../components/BuyerLinking/utils';
import { SEARCH_BUYER_ENTITY_QUERY } from '../data/queries/searchBuyerEntity.gql';

export interface UseBuyerSearchResponse {
  isLoading: boolean;
  options: BuyerSelectOption[];
  searchBuyers: (searchTerm: string) => void;
}

export const useBuyerSearch = (): UseBuyerSearchResponse => {
  const [executeSearch, { data, loading }] = useLazyQuery<
    UsersPageSearchBuyerEntityQuery,
    UsersPageSearchBuyerEntityQueryVariables
  >(SEARCH_BUYER_ENTITY_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const { puppyFilter } = usePuppyMode();

  const searchBuyers = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        executeSearch({
          variables: {
            searchTerm,
            ...(puppyFilter !== undefined && {
              filter: { buyer: { isPuppy: puppyFilter } },
            }),
          },
        });
      }
    },
    [executeSearch, puppyFilter]
  );

  const options: BuyerSelectOption[] = useMemo(() => {
    if (!data?.search?.hits) return [];

    return data.search.hits
      .filter((hit) => hit.entity?.__typename === 'Buyer')
      .map((hit) =>
        getSelectOptionFromBuyer(
          hit.entity as UsersPageBuyerEntityFragmentFragment
        )
      );
  }, [data]);

  return {
    isLoading: loading,
    options,
    searchBuyers,
  };
};
